<template>
	<div class="component-container component-download-rpa-studio">
		<Modal v-if="active" @close="closeDownloadModal">
			<template #header>
				<p class="modal-title">
					Download RPA Studio
				</p>
			</template>
			<template #content>
				<div class="rpa-modal">
					<div>
						<p>Once installed, both the robot and Liberty RPA Studio will be ready on your desktop and you'll be creating process flows in no time.</p>
						<div class="row mt-25">
							<div>
								<label class="rpa-label" for="rpa-version">Version</label>
								<select id="rpa-version" v-model="download_data.version">
									<option v-for="(version_row) in getRpaReleasedVersionsReverse" :key="version_row.release" :value="version_row.release">
										{{ version_row.release }}
									</option>
								</select>
							</div>
							<div>
								<p class="text version-info">
									Latest patch {{ getLatestPatch }}
								</p>
							</div>
						</div>
						<div class="row mt-25">
							<div>
								<p class="rpa-label">
									Release Notes
								</p>
								<div class="release-links">
									<a v-for="(version_row) in getRpaReleasedVersions" :key="version_row.release" :href="`https://docs.netcall.com/docs/liberty-rpa/${version_row.release}/welcome/release-notes`" target="_blank">{{ version_row.release }}</a>
								</div>
							</div>
						</div>
						<div class="row mt-25">
							<div>
								<label class="rpa-label" for="rpa-licence">Bot Licence</label>
								<select id="rpa-licence" v-model="download_data.licence">
									<option value="none">
										None
									</option>
									<option value="new_development">
										New Development Licence
									</option>
									<option disabled>
										──────────────────
									</option>
									<option v-for="bot in bots" :key="bot.id" :value="bot.id">
										{{ bot.name }}
									</option>
								</select>
							</div>
						</div>
					</div>
					<img class="RobotIllustration" alt="Robot Illustration" src="/images/rpaDownload-mini_purple.svg">
				</div>
			</template>
			<template #footer>
				<a class="btn btn-save" :href="getDownloadLink" target="_blank" title="Download RPA Studio" @click="requestDownloadRpaStudio">
					<i class="icon fas fa-download" />
					Download RPA Studio
				</a>
				<a class="btn btn-cancel" href="#" title="Cancel" @click="closeDownloadModal">Cancel</a>
			</template>
		</Modal>
	</div>
</template>

<script setup lang="ts">
	import type { GeneralHelper } from '../helper/general';
	import type { HttpHelper } from '../helper/http';
	import type { BotObject } from '../types/bot';
	import Modal from './Modal.vue';
	import { computed, inject, onMounted, ref, watch } from 'vue';
	import { useStore } from 'vuex';

	const props = defineProps<{
		active: boolean,
		active_bot: any, // eslint-disable-line vue/prop-name-casing
		from_sidebar?: boolean, // eslint-disable-line vue/prop-name-casing
		from_flow_page?: boolean, // eslint-disable-line vue/prop-name-casing
	}>();

	const emit = defineEmits<{
		(event: 'closeDownloadModal'): void,
	}>();

	const store = useStore();
	const http = inject<HttpHelper>('http')!;
	const helper = inject<GeneralHelper>('helper')!;
	const bots = ref<BotObject[]>([]);
	const download_link = ref('/api/studio/download');
	const download_data = ref({
		version: '',
		licence: 'none',
	});

	const getDownloadLink = computed(() => {
		let link = `${download_link.value}?version=${download_data.value.version}`;

		if (Number.isInteger(download_data.value.licence)) {
			link = `/api/rpa/bots/${download_data.value.licence}/download?version=${download_data.value.version}`;
		} else {
			link = `/api/studio/download?version=${download_data.value.version}`;
		}
		if (['none', 'new_development'].includes(download_data.value.licence)) {
			link = `${link}&licence=${download_data.value.licence}`;
		}

		return link;
	});

	const getRpaReleasedVersions = computed(() => {
		return store.state.configuration.rpaReleasedVersions;
	});

	const getRpaReleasedVersionsReverse = computed(() => {
		return helper.deepClone(store.state.configuration.rpaReleasedVersions).reverse();
	});

	const getLastRelease = computed(() => {
		return getRpaReleasedVersions.value.at(-1)?.release || '';
	});

	const getBotId = computed(() => {
		return props.active_bot?.id || 'none';
	});

	const getLatestPatch = computed(() => {
		return getRpaReleasedVersions.value.filter((version: Record<string, any>) => version.release === download_data.value.version)[0]?.latest || 'N/A';
	});

	const isDevelopmentLicenceAvailable = computed(() => {
		return store.state.configuration.licensedDeveloperBots - store.state.configuration.totalDeveloperBots > 0;
	});

	const requestBots = async () => {
		const response = await http.get('/api/rpa/bots');
		if (response.status === 200) {
			const result = await response.json();
			bots.value = result.items;
		}
	};

	const populateDownloadData = () => {
		if (props.from_sidebar === true || props.from_flow_page === true) {
			download_data.value.licence = isDevelopmentLicenceAvailable.value ? 'new_development' : 'none';
		} else {
			download_data.value.licence = getBotId.value;
			download_link.value = `/api/rpa/bots/${props.active_bot?.id}/download`;
		}
		download_data.value.version = getLastRelease.value;
	};

	const requestDownloadRpaStudio = () => {
		emit('closeDownloadModal');
	};

	const closeDownloadModal = () => {
		if (props.from_flow_page === true) {
			window.localStorage.setItem('lc:dashboard_hide_download', 'yes');
		}

		emit('closeDownloadModal');
	};

	watch(() => props.active_bot, () => {
		populateDownloadData();
	});

	onMounted(async () => {
		await requestBots();

		if (props.from_flow_page === true) {
			const show_modal = window.localStorage.getItem('lc:dashboard_hide_download');
			if (show_modal !== null && show_modal === 'yes') {
				closeDownloadModal();
			} else {
				populateDownloadData();
			}
		}
	});
</script>

<style lang="scss" scoped>
	@use '../assets/styles/base' as base;

	.modal-content .rpa-modal {
		display: grid;
		grid-template-columns: 4fr 3fr;
		gap: 2rem;
		margin-bottom: 1rem;
		p {
			font-size: 16px;
			line-height: 1.5;
			margin-bottom: 1rem;
		}
		.RobotIllustration {
			margin-top: -10px;
		}

		.row {
			display: flex;

			.rpa-label {
				display: block;
				margin-bottom: 10px;
				font-weight: 600;
				font-size: 14px;
			}

			.version-info {
				margin-left: 10px;
				padding-top: 32px;
				color: base.$secondary-colour;
			}

			.release-links {
				padding-bottom: 5px;
			}

			.release-links a {
				padding: 5px 10px;
				background-color: rgba(base.$create-color, 0.1);
				outline: 2px dotted base.$create-color;
				outline-offset: -2px;
				display: inline-block;
				width: auto;
				font-family: "Inter", sans-serif;
				font-weight: bold;
				font-size: 12px !important;
				border-radius: 5px;
				line-height: 1.1;
				transition: all ease-in-out .5s;
				margin-right: 0.75rem;
				&:hover {
					background-color: rgba(base.$create-color, 1);
					outline: 2px solid base.$create-color;
					outline-offset: -2px;
					color: white !important;
					transition: all ease-in-out .3s;
				}
			}
		}
	}
</style>
